import { Layout } from 'antd';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { COLOR } from '@/constants';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';
import { PATTERN_DOT_GRID } from '@/assets/brand';

const { Sider } = Layout;

export const StyledLayout = styled(Layout)`
  min-height: 100vh;
`;

/** Keyboard users otherwise tab the whole sidebar before reaching the page. */
export const SkipLink = styled.a`
  position: absolute;
  left: -9999px;
  z-index: 100;
  padding: 10px 16px;
  border-radius: 0 0 8px 0;
  background: ${APP_SHELL_THEME.accent};
  color: #fff;
  font-size: 13px;

  &:focus {
    left: 0;
    top: 0;
  }
`;

export const StyledSider = styled(Sider).attrs({ theme: 'dark' as const })`
  overflow: auto;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: ${COLOR.sider_bg} !important;
  background-image:
    linear-gradient(${COLOR.sider_bg}e6, ${COLOR.sider_bg}e6),
    url(${PATTERN_DOT_GRID}) !important;
  background-size: cover !important;
  background-position: center top !important;
  border-right: 1px solid ${COLOR.sider_border};

  .ant-layout-sider-children {
    position: relative;
    display: flex;
    flex-direction: column;
    /* Full height so the bottom section's auto margin has space to push
       against; without it the column collapses to its content and the
       controls sit directly under the nav list. */
    min-height: 100%;
  }

  .ant-layout-sider-trigger {
    background: ${COLOR.sider_item_active} !important;
    color: ${COLOR.sider_text} !important;
    border-top: 1px solid ${COLOR.sider_border};

    &:hover {
      background: ${COLOR.lavanda} !important;
      color: ${COLOR.white} !important;
    }

    &:focus-visible {
      outline: 2px solid ${COLOR.white};
      outline-offset: -2px;
    }
  }
`;

/** Logo plus the light/dark switch, which sits in the sider's top corner. */
export const SiderTop = styled.div<{ $collapsed?: boolean }>`
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Collapsed, the 80px rail has no room beside the mark, so the switch drops
     under it instead of overlapping. */
  padding-bottom: ${({ $collapsed }) => ($collapsed ? '16px' : '0')};
`;

export const ThemeToggle = styled.button<{ $collapsed?: boolean }>`
  ${({ $collapsed }) =>
    $collapsed
      ? ''
      : `
    position: absolute;
    top: 30px;
    right: 14px;
  `}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${COLOR.sider_text_muted};
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    color: ${COLOR.white};
    background: rgba(255, 255, 255, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${COLOR.white};
    outline-offset: 1px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Trader Controls, User Settings and the profile button.
 *
 * Laid out as the last flex child rather than positioned absolutely. The
 * sidebar scrolls, and an absolutely positioned bottom sits at the bottom of
 * the scrolled content, not of the viewport — so on a short window these
 * controls scrolled away with the nav list and could not be reached until the
 * user scrolled down to find them. `margin-top: auto` pins them to the bottom
 * of the column while the nav list above takes the remaining space.
 */
export const SiderBottomSection = styled.div`
  margin-top: auto;
  flex-shrink: 0;
  padding: 8px 0 24px;
  border-top: 1px solid ${COLOR.sider_border};
  /* Opaque so nav items scrolling underneath don't bleed through. */
  background: ${COLOR.sider_bg};
`;

export const SectionTitle = styled.div`
  padding: 12px 24px 4px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.6px;
  color: ${COLOR.sider_text_muted};
  text-transform: uppercase;
`;

export const BottomNavLink = styled(Link)<{ $active?: boolean; $collapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  gap: 12px;
  /* Matches the nav item height so the rail keeps one hit-target rhythm. */
  min-height: 44px;
  padding: ${({ $collapsed }) => ($collapsed ? '10px 0' : '10px 24px')};
  color: ${({ $active }) => ($active ? COLOR.white : COLOR.sider_text)};
  background: ${({ $active }) => ($active ? COLOR.sider_item_active : 'transparent')};
  /* Inset shadow, not border-left: the accent never reflows the label. */
  box-shadow: ${({ $active }) => ($active ? `inset 3px 0 0 ${COLOR.casa}` : 'none')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  font-size: 14px;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    color: ${COLOR.white};
    background: ${COLOR.sider_item_hover};
  }

  &:focus-visible {
    outline: 2px solid ${COLOR.white};
    outline-offset: -2px;
  }

  .anticon {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: ${({ $active }) => ($active ? COLOR.hacienda : COLOR.sider_text_muted)};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Holds the profile avatar and name.
 *
 * Collapsed, the horizontal padding goes: 24px either side of an 80px rail
 * left a 32px avatar no room to sit on the centre line, so it read as nudged
 * to one side. The name is hidden too — there is nowhere for it to go, and
 * leaving it in pushed the avatar further off centre.
 */
export const ProfileButtonWrap = styled.div<{ $collapsed?: boolean }>`
  padding: ${({ $collapsed }) => ($collapsed ? '16px 0 0' : '16px 24px 0')};
  width: 100%;
  cursor: pointer;

  ${({ $collapsed }) =>
    $collapsed &&
    `
    > div {
      justify-content: center;
      padding: 0 !important;
      gap: 0 !important;
    }

    h5.ant-typography {
      display: none !important;
    }
  `}

  h5.ant-typography {
    color: ${COLOR.sider_text} !important;
    font-weight: 400 !important;
    font-size: 14px !important;
  }

  .ant-avatar {
    border: 1px solid ${COLOR.sider_border};
  }
`;

export const StyledContainer = styled(Layout).attrs({
  className: 'app-shell-main'
})`
  /* Only the margin moves, and only to keep step with the sider's own collapse
     animation. The bare all-property transition this replaces animated
     everything, so a background or colour change anywhere inside faded in
     rather than applying. */
  transition: margin-left 0.2s;
  min-height: 100vh;
  background-color: ${APP_SHELL_THEME.pageBg};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
